import { TextInput, Textarea, Modal, Button, Flex, Title, ActionIcon } from "@mantine/core";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PencilIcon } from "@heroicons/react/20/solid";
import { useMutation } from "@tanstack/react-query";
import HeButton from "../../shared/ui/buttons/HeButton";

import useViewStore from "../../store/view/viewStore";
import useWorkspaceStore from "../../store/workspace/workspaceStore";
import { updateViewInApi } from "../../services/view/viewEndpoints";
import { queryClient, useViews } from "../../hooks/QueryHooks";
import ViewIcon from "../../shared/ui/icons/IconPicker/ViewIcon";
import notify from "../../utils/notify";

// Define interfaces for view data
interface ViewData {
  id: string;
  view_name: string;
  view_description?: string;
  icon_key?: string;
  icon_type?: 'emoji' | 'built_in';
  is_precreated?: boolean;
  filters?: any;
}

interface ViewsResponse {
  data?: {
    views: ViewData[];
  };
  length?: number;
}

const ViewHeaderContent = () => {
  // Get route and store data
  const { viewId } = useParams<{ viewId: string }>();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const { view, setView } = useViewStore((state: any) => ({
    view: state.view,
    setView: state.setView,
  }));
  
  // View data states
  const [name, setName] = useState("");
  const [viewDescription, setViewDescription] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [tempName, setTempName] = useState("");
  const [tempDescription, setTempDescription] = useState("");
  const [selectedIcon, setSelectedIcon] = useState<{ type: 'emoji' | 'built_in', value: string } | undefined>();
  
  // Fetch views for the current workspace
  const { data: viewsData, isSuccess: viewsLoaded } = useViews({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  }) as { data: ViewsResponse | undefined, isSuccess: boolean };
  
  const canEditView = view && !view?.is_precreated;
  
  // Update view mutation
  const updateViewMutation = useMutation({
    mutationFn: updateViewInApi,
  });
  
  // Fetch the current view when route changes
  useEffect(() => {
    if (viewId && viewsLoaded && viewsData?.data?.views?.length) {
      const currentView = viewsData.data.views.find((v: ViewData) => v.id === viewId);
      if (currentView) {
        setView(currentView);
      }
    }
  }, [viewId, viewsLoaded, viewsData, setView]);
  
  // Update local state when view changes
  useEffect(() => {
    if (view) {
      setName(view.view_name || "");
      setViewDescription(view.view_description || "");
      
      // Set icon data
      if (view.icon_key) {
        setSelectedIcon({
          type: view.icon_type === 'emoji' ? 'emoji' : 'built_in',
          value: view.icon_key
        });
      } else {
        setSelectedIcon(undefined);
      }
    }
  }, [view]);
  
  // Handle saving view changes
  const handleViewSave = () => {
    if (!view) return;
    
    // Don't save if name is empty or less than 3 characters
    if (!name || name.trim() === '') {
      notify({
        heading: "View name required",
        type: "failure",
        messageList: ["View name cannot be empty. Please enter a name for your view."],
      });
      setName(view.view_name || 'Untitled View');
      return;
    }
    
    // Validate minimum length
    if (name.trim().length < 3) {
      notify({
        heading: "View name too short",
        type: "failure",
        messageList: ["View name must be at least 3 characters long."],
      });
      return;
    }
    
    // Prepare icon data
    const iconData = selectedIcon ? {
      icon_type: selectedIcon.type,
      icon_key: selectedIcon.value,
    } : {
      icon_type: null,
      icon_key: null
    };
    
    // Process description
    const processedDescription = viewDescription?.trim() === '' ? null : viewDescription;
    
    // Handle filters safely
    const validFilters = view?.filters && 
      typeof view.filters === 'object' && 
      !(view.filters.query_type === null && view.filters.predicates === null) ? 
      view.filters : {};
    
    updateViewMutation.mutate({
      ...view,
      view_name: name.trim(),
      view_description: processedDescription,
      workspace_id: workspace?.id,
      filters: validFilters,
      ...iconData
    }, {
      onSuccess: (data: any) => {
        // Update the cache
        queryClient.setQueryData(["views", workspace?.id], (oldData: any) => {
          if (!oldData) return oldData;
          
          const updatedViews = oldData?.data?.views?.map((item: any) => {
            if (item.id === data?.data?.id) {
              return {
                ...data?.data,
                icon_type: selectedIcon?.type || data?.data?.icon_type,
                icon_key: selectedIcon?.value || data?.data?.icon_key,
                view_icon: selectedIcon?.value || data?.data?.view_icon
              };
            }
            return item;
          });
          
          return {
            ...oldData,
            data: {
              ...oldData.data,
              views: updatedViews,
            },
          };
        });
        
        queryClient.invalidateQueries(["views", workspace?.id]);
      },
      onError: (error: any) => {
        if (error.response?.status !== 0) {
          notify({
            heading: "Unknown error.",
            type: "failure",
            messageList: [
              "An error occurred while updating the view. Please try again later.",
            ],
          });
        }
      },
    });
  };
  
  // Handle icon change
  const handleIconChange = (newIcon: { type: 'emoji' | 'built_in', value: string } | undefined) => {
    if (!canEditView || !view) return;
    
    setSelectedIcon(newIcon);
    
    const iconData = newIcon ? {
      icon_type: newIcon.type,
      icon_key: newIcon.value,
    } : {
      icon_type: null,
      icon_key: null
    };
    
    // Ensure we have valid filters
    const validFilters = view?.filters && 
      typeof view.filters === 'object' && 
      !(view.filters.query_type === null && view.filters.predicates === null) ? 
      view.filters : {};
    
    updateViewMutation.mutate({
      ...view,
      view_name: view.view_name,
      view_description: view.view_description,
      workspace_id: workspace?.id,
      filters: validFilters,
      ...iconData
    }, {
      onSuccess: (data: any) => {
        queryClient.setQueryData(["views", workspace?.id], (oldData: any) => {
          if (!oldData) return oldData;
          
          const updatedViews = oldData?.data?.views?.map((item: any) => {
            if (item.id === data?.data?.id) {
              return {
                ...data?.data,
                icon_type: newIcon?.type,
                icon_key: newIcon?.value,
                view_icon: newIcon?.value
              };
            }
            return item;
          });
          
          return {
            ...oldData,
            data: {
              ...oldData.data,
              views: updatedViews,
            },
          };
        });
        
        queryClient.invalidateQueries(["views", workspace?.id]);
      },
      onError: (error: any) => {
        if (error.response?.status !== 0) {
          notify({
            heading: "Error updating icon",
            type: "failure",
            messageList: [
              "An error occurred while updating the view icon. Please try again.",
            ],
          });
        }
      },
    });
  };

  // Handle modal save
  const handleModalSave = () => {
    setName(tempName);
    setViewDescription(tempDescription);
    setEditModalOpen(false);
    
    // Only save if there were actual changes
    if (tempName !== view?.view_name || tempDescription !== view?.view_description) {
      // We'll call handleViewSave after state updates
      setTimeout(handleViewSave, 0);
    }
  };
  
  // Open edit modal
  const openEditModal = () => {
    if (!canEditView) return;
    setTempName(name);
    setTempDescription(viewDescription);
    setEditModalOpen(true);
  };
  
  if (!view) return null;
  
  // Return the view header UI
  return (
    <>
      <div className="flex items-center gap-3 ml-1">
        {/* Icon section */}
        <div className="flex-shrink-0 flex items-center">
          <ViewIcon
            selectedIcon={selectedIcon}
            onSelect={handleIconChange}
            size={25}
            className={!canEditView ? "" : "cursor-pointer hover:opacity-80"}
            disabled={!canEditView}
          />
        </div>
        
        {/* Name and description display */}
        <div className="flex items-center">
          <div className="flex flex-col gap-0 min-w-0">
            <div className="flex items-center gap-1">
              <span className="text-black text-md h-6 font-semibold">
                {name || "Untitled View"}
              </span>
            </div>
            
            {viewDescription ? (
              <div className="flex items-center gap-1 text-gray-500">
                <span className="text-xs truncate max-w-xs">
                  {viewDescription}
                </span>
              </div>
            ) : canEditView ? (
              <div className="text-xs text-gray-400">
                Add description
              </div>
            ) : null}
          </div>
          
          {/* Edit button */}
          {canEditView && (
            <ActionIcon
              variant="subtle"
              className="ml-1"
              onClick={openEditModal}
              aria-label="Edit view details"
            >
              <PencilIcon className="h-4 w-4 text-gray-500" />
            </ActionIcon>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <Modal 
        opened={editModalOpen} 
        onClose={() => setEditModalOpen(false)}
        title={<Title order={4}>Edit View</Title>}
        centered
        size="md"
        zIndex={1000}
      >
        <div className="flex flex-col gap-4 py-2">
          <div>
            <TextInput
              id="viewName"
              label="View name"
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              placeholder="Enter view name"
              required
              className="w-full"
            />
          </div>
          
          <div>
            <Textarea
              id="viewDescription"
              label="Description (optional)"
              value={tempDescription}
              onChange={(e) => setTempDescription(e.target.value)}
              placeholder="Add a description for your view"
              minRows={3}
              className="w-full"
            />
          </div>
          
          <Flex justify="flex-end" gap="md" mt="md">
            <Button variant="subtle" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <HeButton 
              text="Save Changes" 
              color="purple" 
              onClick={handleModalSave} 
              disabled={!tempName || tempName.trim().length < 3}
            />
          </Flex>
        </div>
      </Modal>
    </>
  );
};

export default ViewHeaderContent;
